Ext.define('SA.data.LocationsCache', {
    extend: 'SA.data.EntityCache',
    singleton: true,

    ItemKeys: ['locationSeries', 'name', 'locationAlias', 'geographicUnit', 'censusReleases', 'geographicReleases'],

    idField: 'locationSeries',

    constructor() {
        this.callParent(arguments);

        SA.data.LocationAliases.on('change', this.onAliasChange, this);
    },

    add(location) {
        if (Array.isArray(location)) {
            location.forEach(this.add, this);
            return;
        }

        this.callParent([this.canonicalize(location)]);
    },

    buildQuery(ids) {
        return {
            fields: this.getQueryFields(),
            type: 'locations',
            where: ['in', 'locationSeries'].concat(ids)
        };
    },

    canonicalize({locationSeries, name, geographicUnit, censusRelease, geographicRelease}) {
        var censusReleases = censusRelease.map(Number),
            geographicReleases = geographicRelease.map(Number),
            locationAlias = SA.data.LocationAliases.get(locationSeries);

        return {locationSeries, name, locationAlias, geographicUnit, censusReleases, geographicReleases};
    },

    getConstituentSeries(location) {
        return this.normalizeLocation(location).locationSeries.slice();
    },

    getGeographicReleases(location) {
        if (!location) {
            return null;
        }

        var {locationSeries} = this.normalizeLocation(location);

        return Ext.Array.intersect(...locationSeries.map(locationSeries => this.get(locationSeries).geographicReleases));
    },

    getGeographicUnit(location) {
        if (!location) {
            return null;
        }

        var locationSeries = this.normalizeLocation(location).locationSeries[0];

        return this.get(locationSeries).geographicUnit;
    },

    getImpl(id) {
        return SA.data.CustomLocations.get(id) || this.callParent([id]);
    },

    getLatestGeographicRelease(location) {
        var normalizedLocation = SA.data.LocationsCache.normalizeLocation(location),
            locations = SA.data.LocationsCache.get(normalizedLocation.locationSeries);

        return Math.max(...Ext.Array.intersect(...locations.map(location => location.geographicReleases)));
    },

    getModel(value) {
        return SA.model.LocationValue.for(value).toModel();
    },

    getPluralGeoUnitName(location) {
        var unit = this.getGeographicUnit(location);

        return SA.data.GeographicUnits.get(unit).name;
    },

    getQueryFields() {
        return ['locationSeries', 'name', 'geographicUnit', 'censusRelease', 'geographicRelease'];
    },

    isComboLocation(location) {
        return this.normalizeLocation(location)?.type === 'combination';
    },

    isCustomLocation(location) {
        return this.toLocationItem(location)?.type === 'customLocation';
    },

    isDMA(location) {
        return !this.isCustomLocation(location) && this.get(location).geographicUnit === 'usa:dma';
    },

    isLocationAvailableInGeographicReleases(locationId, geographicReleases) {
        var location = this.get(this.toId(locationId)),
            isInGeographicReleases = location => SA.Array.overlaps(location.geographicReleases, geographicReleases);

        if (!location) {
            return true;
        }

        geographicReleases = Ext.Array.from(geographicReleases);

        if (location.type === 'combination') {
            return location.locationSeries.every(location => isInGeographicReleases(this.get(location)));
        }
        else if (location.type === 'radius') {
            return isInGeographicReleases(this.get(location.locationSeries));
        }

        return isInGeographicReleases(location);
    },

    locationAvailableInCensusRelease(locationId, censusRelease) {
        var location = this.get(locationId),
            isCombination = location.type === 'combination',
            isRadius = location.type === 'radius',
            isInCensusRelease = location => location.censusReleases.includes(censusRelease);

        if (isCombination) {
            return location.locationSeries.every(locationId => {
                return isInCensusRelease(this.get(locationId));
            });
        }
        else if (isRadius) {
            return isInCensusRelease(this.get(location.locationSeries));
        }

        return isInCensusRelease(location);
    },

    normalizeLocation(value) {
        return SA.model.LocationValue.for(value).toNormalized();
    },

    onAliasChange(locationSeries, alias) {
        var location = this.cache[locationSeries];

        if (location) {
            location.locationAlias = alias;
        }
    },

    purgeLocationsExcept(except) {
        var all = Object.keys(this.cache);

        all.filter(id => !except.includes(id)).forEach(id => delete this.cache[id]);
    },

    sameLocation(a, b) {
        return this.toId(a) === this.toId(b);
    },

    toId(item) {
        return this.toLocationSeries(item);
    },

    toLocationItem(value) {
        return SA.model.LocationValue.for(value).toItem();
    },

    toLocationSeries(value) {
        return SA.model.LocationValue.for(value).toString();
    }
});
